import { createApi } from '@reduxjs/toolkit/query/react';
import { ENDPOINT_CONSTANTS } from '../../constant';
import baseQueryWithReauth from 'service/baseQuery/baseQuery';
import { RoleSkills } from 'pages/job/add/form/skill/jobPostSkillForm.type';

type ApiResponse<T> = {
  data: T;
  status: number;
  message?: string;
};

type Skill = {
  label: string;
  value: string;
};

type Role = Skill & {
  desc: string;
};

export const recruiterQuery = createApi({
  reducerPath: 'recruiterQuery',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['POST', 'INTERN'],
  endpoints: builder => ({
    findSkills: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.SKILLS,
        method: 'GET'
      }),
      transformResponse: <T extends RoleSkills[]>(
        response: ApiResponse<T> | undefined
      ) => {
        const roles: Role[] = [
          {
            label: 'Select',
            value: '',
            desc: ''
          }
        ];
        const skills: Record<string, Skill[]> = {};
        if (response && Array.isArray(response.data)) {
          response.data.forEach(item => {
            roles.push({
              label: item.value,
              value: item._id,
              desc: item.desc
            });
            skills[item._id] = [
              {
                label: 'Select',
                value: ''
              }
            ].concat(
              item.skills.map(({ name, _id }) => ({
                label: name,
                value: _id
              }))
            );
          });
        }
        return {
          roles,
          skills
        };
      }
    }),
    createJobPost: builder.mutation({
      query: body => ({
        url: ENDPOINT_CONSTANTS.JOB_POST,
        method: 'POST',
        body
      }),
      invalidatesTags: ['POST']
    }),
    updateJobPost: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.JOB_POST}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['POST']
    }),
    findJobPost: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.JOB_POST,
        method: 'GET'
      }),
      providesTags: ['POST'],
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    findInternshipPost: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.INTERNSHIP_POST,
        method: 'GET'
      }),
      providesTags: ['INTERN'],
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    createInternship: builder.mutation({
      query: body => ({
        url: ENDPOINT_CONSTANTS.CREATE_INTERNSHIP,
        method: 'POST',
        body
      }),
      invalidatesTags: ['INTERN']
    }),
    updateInternship: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.CREATE_INTERNSHIP}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['INTERN']
    }),
    updateJobAsClose: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.CLOSE_JOB_POST}/${id}`,
        method: 'PUT',
        body
      })
    }),
    updateJobAsOpen: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.OPEN_JOB_POST}/${id}`,
        method: 'PUT',
        body
      })
    }),
    findOneJobPost: builder.query({
      query: id => ({
        url: `${ENDPOINT_CONSTANTS.JOB_POST}/${id}`,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    findRecruiterCandidate: builder.query({
      query: params => {
        let url = ENDPOINT_CONSTANTS.RECRUITER_CANDIDATE;
        if (params) {
          url += `?${new URLSearchParams(params).toString()}`;
        }
        return {
          url,
          method: 'GET'
        };
      },
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    findRecruiterDashboardStats: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.RECRUITER_DASH_STATS,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    findRecruiterDashboardInterview: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.RECRUITER_DASH_INTERVIEW,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    })
  })
});

export const {
  useFindSkillsQuery,
  useCreateJobPostMutation,
  useFindJobPostQuery,
  useFindOneJobPostQuery,
  useFindInternshipPostQuery,
  useCreateInternshipMutation,
  useUpdateInternshipMutation,
  useUpdateJobPostMutation,
  useUpdateJobAsCloseMutation,
  useUpdateJobAsOpenMutation,
  useFindRecruiterCandidateQuery,
  useFindRecruiterDashboardStatsQuery,
  useFindRecruiterDashboardInterviewQuery
} = recruiterQuery;
